import { FetchState } from "../FetchState"

export interface IReportData<T> {
    state: FetchState;
    data: T[];
    fetchDate: Date;
    errorMessage: string;
}

export function emptyReportData<T>(): IReportData<T> {
    return {
        state: FetchState.NotFetched,
        data: [],
        fetchDate: new Date(),
        errorMessage: ""
    };
}

export interface IReportDataObject<T> {
    state: FetchState;
    data: T;
    fetchDate: Date;
    errorMessage: string;
}

export function emptyReportDataObject<T>(initial?: T): IReportDataObject<T> {
    return {
        state: FetchState.NotFetched,
        data: initial || {} as T,
        fetchDate: new Date(),
        errorMessage: ""
    };
}

/**
 * Extended report data interface for paginated data with infinite scroll support
 * Note: This interface contains only serializable state data.
 * The loadMore function should be passed separately as a component prop.
 */
export interface IPaginatedReportData<T> extends IReportData<T> {
    currentPage: number;
    pageSize: number;
    hasMore: boolean;
    loadingMoreState: FetchState;
    totalCount?: number;
}

export function emptyPaginatedReportData<T>(pageSize: number = 25): IPaginatedReportData<T> {
    return {
        state: FetchState.NotFetched,
        data: [],
        fetchDate: new Date(),
        errorMessage: "",
        currentPage: 1,
        pageSize: pageSize,
        hasMore: true,
        loadingMoreState: FetchState.NotFetched,
        totalCount: undefined
    };
}

/**
 * Type guard to check if IReportData is actually IPaginatedReportData
 */
export function isPaginatedReportData<T>(data: IReportData<T> | IPaginatedReportData<T>): data is IPaginatedReportData<T> {
    return 'currentPage' in data && 'pageSize' in data && 'hasMore' in data;
}

/**
 * Helper function to determine if more data can be loaded
 */
export function canLoadMore<T>(paginatedData: IPaginatedReportData<T>): boolean {
    return paginatedData.hasMore 
        && paginatedData.loadingMoreState !== FetchState.Busy
        && paginatedData.state === FetchState.Success;
}

